import React, { useEffect, useState, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes, faCheck, faUndo, faEye, faSpinner,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "./AdminLayout";
import "./admin.css";

function adminFetch(path, opts = {}) {
  const token = localStorage.getItem("adminToken");
  const headers = {
    Authorization: `Bearer ${token}`,
    ...(opts.headers || {}),
  };
  const hasContentTypeHeader = Object.keys(headers).some((key) => key.toLowerCase() === "content-type");
  const isFormDataBody = typeof FormData !== "undefined" && opts.body instanceof FormData;

  if (!hasContentTypeHeader && !isFormDataBody) {
    headers["Content-Type"] = "application/json";
  }

  return fetch(path, {
    ...opts,
    headers,
  });
}

const STATUS_TABS = [
  { key: "pending",  label: "Pending"   },
  { key: "approved", label: "Approved"  },
  { key: "rejected", label: "Rejected"  },
  { key: "resubmit", label: "Resubmit"  },
  { key: "mismatch", label: "Mismatch Review" },
  { key: "all",      label: "All"       },
];

// Fetch KYC document as blob URL so auth header is sent
async function fetchDocUrl(kycId, field) {
  const token = localStorage.getItem("adminToken");
  try {
    const r = await fetch(`/api/admin/kyc-document/${kycId}/${field}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    const blob = await r.blob();
    return {
      url: URL.createObjectURL(blob),
      mime: (blob && blob.type) || "",
    };
  } catch {
    return null;
  }
}

async function openDocInNewTab(kycId, field) {
  const token = localStorage.getItem("adminToken");
  try {
    const r = await fetch(`/api/admin/kyc-document/${kycId}/${field}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch {
    // no-op
  }
}

async function reuploadKycDocument(kycId, field, file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await adminFetch(`/api/admin/kyc/${kycId}/document/${field}/reupload`, {
    method: "POST",
    body: formData,
  });
  let payload = {};
  try {
    payload = await res.json();
  } catch {
    payload = {};
  }
  return { ok: res.ok, payload };
}

function KycStatusBadge({ status }) {
  return <span className={`adm-badge ${status}`}>{status?.replace("_", " ") || "—"}</span>;
}

function OcrStatusBadge({ status }) {
  const label = status?.replace(/_/g, " ") || "—";
  return <span className={`adm-badge ${status || ""}`}>{label}</span>;
}

function formatMismatchScore(score) {
  if (score === null || score === undefined || score === "") return "—";
  const numeric = Number(score);
  if (Number.isNaN(numeric)) return "—";
  return `${numeric.toFixed(0)}%`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatDisplayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatLabel(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) || "—";
}

function ReviewStat({ label, value, tone = "default" }) {
  return (
    <div className={`adm-review-stat ${tone}`}>
      <div className="adm-review-stat-value">{value}</div>
      <div className="adm-review-stat-label">{label}</div>
    </div>
  );
}

function ReviewInfoGrid({ items, columns = 2 }) {
  return (
    <div className={`adm-review-info-grid cols-${columns}`}>
      {items.map((item) => (
        <div className="adm-review-info-item" key={item.label}>
          <div className="adm-review-info-label">{item.label}</div>
          <div className="adm-review-info-value">{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function ReviewTimeline({ title, items, emptyText }) {
  return (
    <div className="adm-review-section">
      <div className="adm-review-section-head">
        <h5>{title}</h5>
      </div>
      {items.length === 0 ? (
        <div className="adm-review-empty-note">{emptyText}</div>
      ) : (
        <div className="adm-review-timeline">
          {items.map((item) => (
            <div className="adm-review-timeline-item" key={`${title}-${item.id}`}>
              <div className="adm-review-timeline-dot" />
              <div className="adm-review-timeline-content">
                <div className="adm-review-timeline-top">
                  <strong>{item.title}</strong>
                  <span>{formatDateTime(item.when)}</span>
                </div>
                {item.meta && <div className="adm-review-timeline-meta">{item.meta}</div>}
                {item.notes && <div className="adm-review-timeline-notes">{item.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocGrid({ kycId, documents, onDocumentUpdated }) {
  const [urls,    setUrls]    = useState({});
  const [loading, setLoading] = useState(true);
  const [replacementFiles, setReplacementFiles] = useState({});
  const [uploadingField, setUploadingField] = useState("");
  const [uploadFeedback, setUploadFeedback] = useState({});
  const urlsRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        (documents || []).map(async ({ field, uploaded }) => {
          if (!uploaded) return [field, null];
          return [field, await fetchDocUrl(kycId, field)];
        })
      );
      if (!cancelled) {
        const map = Object.fromEntries(results);
        urlsRef.current = map;
        setUrls(map);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      Object.values(urlsRef.current).forEach((entry) => {
        if (entry && entry.url) URL.revokeObjectURL(entry.url);
      });
    };
  }, [kycId, documents]);

  if (loading) return <p style={{ fontSize: ".82rem", color: "var(--adm-muted)" }}>Loading documents…</p>;

  const onPickReplacement = (field, file) => {
    setReplacementFiles((current) => ({ ...current, [field]: file || null }));
    setUploadFeedback((current) => ({ ...current, [field]: "" }));
  };

  const onUploadReplacement = async (field) => {
    const selected = replacementFiles[field];
    if (!selected) {
      setUploadFeedback((current) => ({ ...current, [field]: "Please choose a file first." }));
      return;
    }

    setUploadingField(field);
    setUploadFeedback((current) => ({ ...current, [field]: "" }));
    try {
      const result = await reuploadKycDocument(kycId, field, selected);
      if (!result.ok) {
        setUploadFeedback((current) => ({ ...current, [field]: result.payload?.message || "Upload failed." }));
        return;
      }

      setUploadFeedback((current) => ({ ...current, [field]: "Document replaced successfully." }));
      setReplacementFiles((current) => ({ ...current, [field]: null }));
      if (typeof onDocumentUpdated === "function") {
        await onDocumentUpdated();
      }
    } catch {
      setUploadFeedback((current) => ({ ...current, [field]: "Upload failed. Please try again." }));
    } finally {
      setUploadingField("");
    }
  };

  return (
    <div className="adm-doc-grid adm-doc-grid-rich">
      {(documents || []).map(({ field, label, uploaded, filename, serverFileAvailable }) => {
        const entry = urls[field];
        const isImage = Boolean(entry && String(entry.mime || "").startsWith("image/"));
        const isPdf = Boolean(entry && String(entry.mime || "").toLowerCase() === "application/pdf");
        const isUploading = uploadingField === field;
        const selectedFile = replacementFiles[field] || null;
        const feedback = uploadFeedback[field] || "";

        if (!uploaded) {
          return (
            <div className="adm-doc-thumb adm-doc-card" key={field}>
              <div className="adm-doc-thumb-label">{label}</div>
              <div className="adm-doc-meta">Not uploaded by applicant</div>
              <div className="adm-doc-loading">Missing</div>
            </div>
          );
        }

        if (entry && isImage) {
          return (
            <div className="adm-doc-thumb adm-doc-card" key={field}>
              <div className="adm-doc-thumb-label">{label}</div>
              <div className="adm-doc-meta">{filename || "Image"}</div>
              <img src={entry.url} alt={label} />
              <div className="adm-doc-actions">
                <button className="adm-btn ghost sm" type="button" onClick={() => openDocInNewTab(kycId, field)}>
                  Preview full file
                </button>
              </div>
            </div>
          );
        }

        if (entry && isPdf) {
          return (
            <div className="adm-doc-thumb adm-doc-card" key={field}>
              <div className="adm-doc-thumb-label">{label}</div>
              <div className="adm-doc-meta">{filename || "PDF Document"}</div>
              <iframe
                src={entry.url}
                title={label}
                className="adm-doc-preview-pdf"
                style={{ width: "100%", height: "300px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
              <div className="adm-doc-actions">
                <button className="adm-btn ghost sm" type="button" onClick={() => openDocInNewTab(kycId, field)}>
                  Preview full file
                </button>
              </div>
            </div>
          );
        }

        return (
          <div className="adm-doc-thumb adm-doc-card" key={field}>
            <div className="adm-doc-thumb-label">{label}</div>
            <div className="adm-doc-meta">{filename || "Uploaded file"}</div>
            <div className="adm-doc-loading" style={{ marginBottom: ".4rem" }}>
              {serverFileAvailable ? "Uploaded" : "Stored path found, preview unavailable"}
            </div>
            {!serverFileAvailable && (
              <div className="adm-doc-repair-wrap">
                <input
                  className="adm-doc-repair-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  onChange={(e) => onPickReplacement(field, e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                />
                <button
                  className="adm-btn sm"
                  type="button"
                  disabled={!selectedFile || isUploading}
                  onClick={() => onUploadReplacement(field)}
                >
                  {isUploading ? "Uploading..." : "Upload replacement"}
                </button>
                {feedback ? <div className="adm-doc-repair-note">{feedback}</div> : null}
              </div>
            )}
            <div className="adm-doc-actions">
              <button
                className="adm-btn ghost sm"
                type="button"
                onClick={() => openDocInNewTab(kycId, field)}
              >
                Open file
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReviewPanel({ item, onClose, onDone }) {
  const [detail, setDetail]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState({ type: "", text: "" });
  const [mismatchNotes, setMismatchNotes] = useState({});

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [reason,         setReason]         = useState("");
  const [allowResubmit,  setAllowResubmit]  = useState(true);
  const [reviewerNotes,  setReviewerNotes]  = useState("");

  const refreshDetail = useCallback(async () => {
    const res = await adminFetch(`/api/admin/kyc/${item.id}/review-details`);
    const data = await res.json();
    if (res.ok) setDetail(data);
  }, [item.id]);

  useEffect(() => {
    setMsg({ type: "", text: "" });
    setShowRejectForm(false);
    setReason(""); setAllowResubmit(true); setReviewerNotes(""); setMismatchNotes({});
    (async () => {
      setLoading(true);
      try {
        const res  = await adminFetch(`/api/admin/kyc/${item.id}/review-details`);
        const data = await res.json();
        if (res.ok) setDetail(data);
      } finally { setLoading(false); }
    })();
  }, [item.id]);

  const doApprove = async () => {
    if (!window.confirm(`Approve KYC for ${item.full_name || item.email}?`)) return;
    setSaving(true);
    try {
      const res  = await adminFetch(`/api/admin/kyc/${item.id}/approve`, { method: "PATCH" });
      const data = await res.json();
      if (res.ok) { setMsg({ type: "success", text: "KYC approved successfully." }); onDone(); }
      else          setMsg({ type: "error",   text: data.message || "Failed." });
    } finally { setSaving(false); }
  };

  const doReject = async () => {
    if (!reason.trim()) { setMsg({ type: "error", text: "Rejection reason is required." }); return; }
    setSaving(true);
    try {
      const res = await adminFetch(`/api/admin/kyc/${item.id}/reject`, {
        method: "PATCH",
        body: JSON.stringify({
          rejection_reason: reason.trim(),
          allow_resubmit:   allowResubmit,
          reviewer_notes:   reviewerNotes.trim() || null,
        }),
      });
      const data = await res.json();
      if (res.ok) { setMsg({ type: "success", text: data.message }); onDone(); }
      else          setMsg({ type: "error",   text: data.message || "Failed." });
    } finally { setSaving(false); }
  };

  const doMismatchAction = async (mismatchId, action, reviewerNotes = "") => {
    setSaving(true);
    try {
      const res = await adminFetch(`/api/admin/kyc/mismatch/${mismatchId}/${action}`, {
        method: "PATCH",
        body: JSON.stringify({ reviewer_notes: reviewerNotes || null }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: data.message || "Mismatch reviewed." });
        setMismatchNotes((current) => ({ ...current, [mismatchId]: "" }));
        await refreshDetail();
      } else {
        setMsg({ type: "error", text: data.message || "Failed." });
      }
    } finally {
      setSaving(false);
    }
  };

  const finalizeMismatchReview = async (approvalAction) => {
    if (!window.confirm(`Finalize this KYC as ${approvalAction === "approve" ? "approved" : "sent back for resubmission"}?`)) return;
    setSaving(true);
    try {
      const res = await adminFetch(`/api/admin/kyc/${item.id}/finalize-mismatch-review`, {
        method: "PATCH",
        body: JSON.stringify({ approval_action: approvalAction }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: data.message || "Mismatch review finalized." });
        onDone();
      } else {
        setMsg({ type: "error", text: data.message || "Failed." });
      }
    } finally {
      setSaving(false);
    }
  };

  const d = detail?.kyc || null;
  const mismatchList = detail?.mismatches || [];
  const ocrExtraction = detail?.ocrExtraction || null;
  const mismatchSummary = detail?.mismatchSummary || {};
  const documents = d?.documents || [];
  const auditItems = (detail?.auditTrail || []).map((entry) => ({
    id: `audit-${entry.id}`,
    title: formatLabel(entry.action),
    when: entry.created_at,
    meta: [entry.actor_firstname, entry.actor_lastname].filter(Boolean).join(" ") || entry.actor_email || "System",
    notes: [
      entry.old_status ? `From ${formatLabel(entry.old_status)}` : null,
      entry.new_status ? `to ${formatLabel(entry.new_status)}` : null,
      entry.notes || null,
    ].filter(Boolean).join(" · "),
  }));
  const mismatchAuditItems = (detail?.mismatchAuditTrail || []).map((entry) => ({
    id: `mismatch-audit-${entry.id}`,
    title: `${formatLabel(entry.action)}${entry.field_name ? `: ${formatLabel(entry.field_name)}` : ""}`,
    when: entry.created_at,
    meta: [entry.actor_firstname, entry.actor_lastname].filter(Boolean).join(" ") || entry.actor_email || "System",
    notes: [entry.notes, entry.new_status ? `Status ${formatLabel(entry.new_status)}` : null].filter(Boolean).join(" · "),
  }));
  const reviewComplete = mismatchSummary.pending === 0;
  const canShowManualActions = d && (d.status === "pending" || d.status === "resubmit") && d.ocrStatus !== "mismatch_flagged";
  const canFinalizeMismatch = d && (d.ocrStatus === "mismatch_flagged" || d.status === "resubmit");

  return (
    <div className="adm-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-panel">
        <div className="adm-panel-header">
          <h4>KYC Review Workspace</h4>
          <button className="adm-panel-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="adm-panel-body">
          {loading && <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>}
          {msg.text && <div className={`adm-alert ${msg.type}`}>{msg.text}</div>}

          {d && !loading && (
            <>
              <div className="adm-review-hero">
                <div>
                  <div className="adm-review-kicker">Case #{d.id}</div>
                  <h5>{d.fullName || d.applicant?.name || "KYC Submission"}</h5>
                  <div className="adm-review-hero-meta">
                    <span>{formatLabel(d.role)}</span>
                    <span>{formatLabel(d.documentType)}</span>
                    <span>Submitted {formatDateTime(d.submittedAt)}</span>
                  </div>
                </div>
                <div className="adm-review-badges">
                  <KycStatusBadge status={d.status} />
                  {d.ocrStatus ? <OcrStatusBadge status={d.ocrStatus} /> : null}
                </div>
              </div>

              <div className="adm-review-summary-grid">
                <ReviewStat label="Documents" value={`${documents.filter((doc) => doc.uploaded).length}/${documents.length}`} tone="info" />
                <ReviewStat label="Mismatch Score" value={formatMismatchScore(d.mismatchScore)} tone="warning" />
                <ReviewStat label="Pending Mismatches" value={String(mismatchSummary.pending || 0)} tone={(mismatchSummary.pending || 0) > 0 ? "danger" : "success"} />
                <ReviewStat label="OCR Engine" value={ocrExtraction?.ocr_engine || "—"} tone="default" />
              </div>

              <div className="adm-review-section">
                <div className="adm-review-section-head">
                  <h5>Case Summary</h5>
                </div>
                <ReviewInfoGrid
                  columns={2}
                  items={[
                    { label: "Submission Status", value: <KycStatusBadge status={d.status} /> },
                    { label: "OCR Status", value: d.ocrStatus ? <OcrStatusBadge status={d.ocrStatus} /> : "—" },
                    { label: "Submitted At", value: formatDateTime(d.submittedAt) },
                    { label: "Last Updated", value: formatDateTime(d.updatedAt) },
                    { label: "Reviewed At", value: formatDateTime(d.reviewedAt) },
                    { label: "Assigned Reviewer", value: d.reviewOwner?.name || "Unreviewed" },
                    { label: "Applicant Account Status", value: formatLabel(d.applicant?.accountKycStatus) },
                    { label: "Verified At", value: formatDateTime(d.applicant?.kycVerifiedAt) },
                  ]}
                />
                {(d.rejectionReason || d.mismatchReason || d.reviewerNotes) && (
                  <div className="adm-review-note-stack">
                    {d.rejectionReason ? <div className="adm-alert error"><strong>Applicant-facing reason:</strong> {d.rejectionReason}</div> : null}
                    {d.mismatchReason ? <div className="adm-alert info"><strong>Mismatch summary:</strong> {d.mismatchReason}</div> : null}
                    {d.reviewerNotes ? <div className="adm-alert"><strong>Internal reviewer notes:</strong> {d.reviewerNotes}</div> : null}
                  </div>
                )}
              </div>

              <div className="adm-review-section">
                <div className="adm-review-section-head">
                  <h5>Applicant And Submission Data</h5>
                </div>
                <ReviewInfoGrid
                  columns={2}
                  items={[
                    { label: "Applicant Name", value: formatDisplayValue(d.applicant?.name || d.fullName) },
                    { label: "User ID", value: formatDisplayValue(d.userId) },
                    { label: "Account Email", value: formatDisplayValue(d.applicant?.email) },
                    { label: "Contact Email", value: formatDisplayValue(d.contactEmail) },
                    { label: "Account Phone", value: formatDisplayValue(d.applicant?.phone) },
                    { label: "Contact Phone", value: formatDisplayValue(d.contactPhone) },
                    { label: "Country Code", value: formatDisplayValue(d.countryCode) },
                    { label: "Submission IP", value: formatDisplayValue(d.submittedIp) },
                  ]}
                />
                <ReviewInfoGrid
                  columns={2}
                  items={[
                    { label: "Document Type", value: formatLabel(d.documentType) },
                    { label: "Document Number", value: formatDisplayValue(d.documentNumber) },
                    { label: "Date of Birth", value: formatDisplayValue(d.dateOfBirth) },
                    { label: "Role Applied As", value: formatLabel(d.role) },
                    { label: "Current Address", value: formatDisplayValue(d.currentAddress) },
                  ]}
                />
              </div>

              <div className="adm-review-section">
                <div className="adm-review-section-head">
                  <h5>Document Center</h5>
                  <span>{documents.filter((doc) => doc.uploaded).length} uploaded</span>
                </div>
                <DocGrid kycId={d.id} documents={documents} onDocumentUpdated={refreshDetail} />
              </div>

              {(ocrExtraction || mismatchList.length > 0) && (
                <div className="adm-review-section">
                  <div className="adm-review-section-head">
                    <h5>OCR And AI Validation</h5>
                    <span>{mismatchSummary.total || 0} mismatch items</span>
                  </div>
                  {ocrExtraction && (
                    <>
                      <ReviewInfoGrid
                        columns={2}
                        items={[
                          { label: "Engine", value: formatDisplayValue(ocrExtraction.ocr_engine) },
                          { label: "Overall Confidence", value: formatMismatchScore(ocrExtraction.overall_confidence) },
                          { label: "Extraction Time", value: ocrExtraction.extraction_time_ms ? `${ocrExtraction.extraction_time_ms} ms` : "—" },
                          { label: "Extracted At", value: formatDateTime(ocrExtraction.created_at) },
                          { label: "Name Confidence", value: formatMismatchScore(ocrExtraction.name_confidence) },
                          { label: "Citizenship Confidence", value: formatMismatchScore(ocrExtraction.citizenship_number_confidence) },
                          { label: "DOB Confidence", value: formatMismatchScore(ocrExtraction.dob_confidence) },
                          { label: "Address Confidence", value: formatMismatchScore(ocrExtraction.address_confidence) },
                        ]}
                      />
                      <div className="adm-review-compare-grid">
                        <div className="adm-review-compare-card">
                          <div className="adm-review-compare-label">Extracted Name</div>
                          <div>{formatDisplayValue(ocrExtraction.extracted_name)}</div>
                        </div>
                        <div className="adm-review-compare-card">
                          <div className="adm-review-compare-label">Extracted Citizenship Number</div>
                          <div>{formatDisplayValue(ocrExtraction.extracted_citizenship_number)}</div>
                        </div>
                        <div className="adm-review-compare-card">
                          <div className="adm-review-compare-label">Extracted Date Of Birth</div>
                          <div>{formatDisplayValue(ocrExtraction.extracted_date_of_birth)}</div>
                        </div>
                        <div className="adm-review-compare-card">
                          <div className="adm-review-compare-label">Extracted Address</div>
                          <div>{formatDisplayValue(ocrExtraction.extracted_address)}</div>
                        </div>
                      </div>
                      {ocrExtraction.raw_ocr_text ? (
                        <details className="adm-review-details-block">
                          <summary>Show raw OCR / AI text</summary>
                          <pre className="adm-review-pre">{ocrExtraction.raw_ocr_text}</pre>
                        </details>
                      ) : null}
                    </>
                  )}

                  {mismatchList.length > 0 ? (
                    <div className="adm-review-mismatch-list">
                      {mismatchList.map((mismatch) => {
                        const mismatchReviewer = [mismatch.reviewer_firstname, mismatch.reviewer_lastname].filter(Boolean).join(" ") || mismatch.reviewer_email || "—";
                        return (
                          <div key={mismatch.id} className="adm-review-mismatch-card">
                            <div className="adm-review-mismatch-head">
                              <div>
                                <h6>{formatLabel(mismatch.field_name)}</h6>
                                <div className="adm-review-mismatch-submeta">
                                  Confidence {formatMismatchScore(mismatch.confidence_score)}
                                </div>
                              </div>
                              <div className="adm-review-badges">
                                <span className={`adm-badge ${mismatch.mismatch_severity || ""}`}>{formatLabel(mismatch.mismatch_severity || "medium")}</span>
                                <span className={`adm-badge ${mismatch.review_status || "pending"}`}>{formatLabel(mismatch.review_status)}</span>
                              </div>
                            </div>
                            <div className="adm-review-compare-grid">
                              <div className="adm-review-compare-card user">
                                <div className="adm-review-compare-label">User Submitted</div>
                                <div>{formatDisplayValue(mismatch.user_submitted_value)}</div>
                              </div>
                              <div className="adm-review-compare-card system">
                                <div className="adm-review-compare-label">OCR / AI Extracted</div>
                                <div>{formatDisplayValue(mismatch.ocr_extracted_value)}</div>
                              </div>
                            </div>
                            <ReviewInfoGrid
                              columns={2}
                              items={[
                                { label: "Reviewed By", value: formatDisplayValue(mismatchReviewer) },
                                { label: "Reviewed At", value: formatDateTime(mismatch.reviewed_at) },
                              ]}
                            />
                            {mismatch.reviewer_notes ? (
                              <div className="adm-review-inline-note"><strong>Existing notes:</strong> {mismatch.reviewer_notes}</div>
                            ) : null}
                            {mismatch.review_status === "pending" ? (
                              <>
                                <div className="adm-field">
                                  <label>Reviewer notes for this mismatch</label>
                                  <textarea
                                    rows={2}
                                    value={mismatchNotes[mismatch.id] || ""}
                                    onChange={(e) => setMismatchNotes((current) => ({ ...current, [mismatch.id]: e.target.value }))}
                                    placeholder="Add field-level review notes before approving or rejecting..."
                                  />
                                </div>
                                <div className="adm-review-action-row">
                                  <button className="adm-btn success sm" disabled={saving} onClick={() => doMismatchAction(mismatch.id, "approve", mismatchNotes[mismatch.id] || "") }>
                                    <FontAwesomeIcon icon={faCheck} />
                                    Accept mismatch
                                  </button>
                                  <button className="adm-btn danger sm" disabled={saving} onClick={() => doMismatchAction(mismatch.id, "reject", mismatchNotes[mismatch.id] || "") }>
                                    <FontAwesomeIcon icon={faTimes} />
                                    Mark for correction
                                  </button>
                                </div>
                              </>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="adm-review-empty-note">No field mismatches are recorded for this case.</div>
                  )}
                </div>
              )}

              {canShowManualActions && (
                <>
                  <div className="adm-review-section">
                    <div className="adm-review-section-head">
                      <h5>Review Decision</h5>
                    </div>

                    {!showRejectForm ? (
                    <div style={{ display: "flex", gap: ".6rem", marginTop: ".25rem", flexWrap: "wrap" }}>
                      <button className="adm-btn success" disabled={saving} onClick={doApprove}>
                        {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                        Approve
                      </button>
                      <button
                        className="adm-btn danger"
                        disabled={saving}
                        onClick={() => { setShowRejectForm(true); setMsg({ type: "", text: "" }); }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                        Reject / Resubmit
                      </button>
                    </div>
                    ) : (
                    <div style={{ marginTop: ".25rem" }}>
                      <p style={{ fontSize: ".8rem", fontWeight: 700, color: "var(--adm-danger)", marginBottom: ".6rem" }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: ".35rem" }} />
                        Rejection Details
                      </p>
                      <div className="adm-field">
                        <label>Reason (shown to applicant) *</label>
                        <textarea
                          rows={3}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Explain why the KYC was rejected…"
                        />
                      </div>
                      <div className="adm-field">
                        <label>Reviewer notes (internal only)</label>
                        <textarea
                          rows={2}
                          value={reviewerNotes}
                          onChange={(e) => setReviewerNotes(e.target.value)}
                          placeholder="Optional internal notes…"
                        />
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".84rem", marginBottom: ".75rem", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={allowResubmit}
                          onChange={(e) => setAllowResubmit(e.target.checked)}
                        />
                        Allow user to resubmit
                      </label>
                      <div style={{ display: "flex", gap: ".6rem" }}>
                        <button className="adm-btn danger" disabled={saving} onClick={doReject}>
                          {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTimes} />}
                          {allowResubmit ? "Reject & Allow Resubmit" : "Reject Permanently"}
                        </button>
                        <button className="adm-btn ghost" disabled={saving} onClick={() => setShowRejectForm(false)}>
                          <FontAwesomeIcon icon={faUndo} />
                          Cancel
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                </>
              )}

              {canFinalizeMismatch && (
                <div className="adm-review-section">
                  <div className="adm-review-section-head">
                    <h5>Mismatch Finalization</h5>
                    <span>{reviewComplete ? "All mismatch items reviewed" : `${mismatchSummary.pending || 0} items still pending`}</span>
                  </div>
                  {!reviewComplete && (
                    <div className="adm-alert info">Review each mismatch item before finalizing the case to avoid sending an incomplete decision back to the applicant.</div>
                  )}
                  <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap" }}>
                    <button className="adm-btn success" disabled={saving} onClick={() => finalizeMismatchReview("approve")}>
                      <FontAwesomeIcon icon={faCheck} />
                      Approve after review
                    </button>
                    <button className="adm-btn danger" disabled={saving} onClick={() => finalizeMismatchReview("resubmit")}>
                      <FontAwesomeIcon icon={faUndo} />
                      Send back for resubmission
                    </button>
                  </div>
                </div>
              )}

              <ReviewTimeline title="KYC Audit Trail" items={auditItems} emptyText="No case-level audit records found for this submission yet." />
              <ReviewTimeline title="Mismatch Audit Trail" items={mismatchAuditItems} emptyText="No mismatch-level audit activity has been recorded yet." />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminKycQueue() {
  const [statusTab, setStatusTab] = useState("pending");
  const [rows,      setRows]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [listError, setListError] = useState("");

  const load = useCallback(async (tab, pg) => {
    setLoading(true);
    setListError("");
    try {
      const endpoint = tab === "mismatch"
        ? `/api/admin/kyc/mismatch-queue?page=${pg}&limit=20`
        : `/api/admin/kyc?status=${tab}&page=${pg}&limit=20`;
      const res  = await adminFetch(endpoint);
      const data = await res.json();
      if (res.ok) {
        setRows(tab === "mismatch" ? (data.mismatches || []) : (data.submissions || []));
        setTotal(data.total || 0);
      } else if (res.status === 401 || res.status === 403) {
        setListError("Session expired or invalid. Please log out and log in again.");
        setRows([]);
      } else {
        setListError(data?.message || `Error loading submissions (HTTP ${res.status}).`);
        setRows([]);
      }
    } catch (err) {
      setListError("Could not reach the server. Check that the backend is running.");
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(statusTab, page); }, [statusTab, page, load]);

  const switchTab = (t) => { setStatusTab(t); setPage(1); setSelected(null); };
  const handleDone = () => { setSelected(null); load(statusTab, page); };

  const totalPages = Math.ceil(total / 20) || 1;

  return (
    <AdminLayout pageTitle="KYC Queue">
      <div className="adm-filter-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            className={`adm-filter-tab${statusTab === t.key ? " active" : ""}`}
            onClick={() => switchTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <h3>Submissions</h3>
          <span style={{ fontSize: ".8rem", color: "var(--adm-muted)" }}>
            {total} total
          </span>
        </div>

        {loading && (
          <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>
        )}

        {!loading && listError && (
          <div className="adm-card-body">
            <div className="adm-alert error" style={{ margin: 0 }}>{listError}</div>
          </div>
        )}

        {!loading && !listError && rows.length === 0 && (
          <div className="adm-empty">
            <p>No {statusTab === "all" ? "" : statusTab} submissions.</p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    {statusTab === "mismatch" ? (
                      <>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>OCR Status</th>
                        <th>Mismatches</th>
                        <th>Score</th>
                        <th>Submitted</th>
                        <th></th>
                      </>
                    ) : (
                      <>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Document</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th></th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="clickable" onClick={() => setSelected(r)}>
                      {statusTab === "mismatch" ? (
                        <>
                          <td style={{ color: "var(--adm-muted)", fontSize: ".78rem" }}>{r.id}</td>
                          <td>{r.full_name || `${r.firstname || ""} ${r.lastname || ""}`.trim()}</td>
                          <td style={{ fontSize: ".82rem", color: "var(--adm-muted)" }}>{r.email}</td>
                          <td><OcrStatusBadge status={r.ocr_status} /></td>
                          <td style={{ fontSize: ".82rem" }}>{r.mismatch_count || 0}</td>
                          <td style={{ fontSize: ".82rem" }}>{formatMismatchScore(r.mismatch_score)}</td>
                          <td style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                          <td>
                            <button
                              className="adm-btn ghost sm"
                              onClick={(e) => { e.stopPropagation(); setSelected(r); }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                              Review
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ color: "var(--adm-muted)", fontSize: ".78rem" }}>{r.id}</td>
                          <td>{r.full_name || `${r.firstname} ${r.lastname}`}</td>
                          <td style={{ fontSize: ".82rem", color: "var(--adm-muted)" }}>{r.email}</td>
                          <td style={{ textTransform: "capitalize" }}>{r.role}</td>
                          <td style={{ textTransform: "capitalize", fontSize: ".82rem" }}>{r.document_type?.replace("_", " ")}</td>
                          <td style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                          <td><KycStatusBadge status={r.status} /></td>
                          <td>
                            <button
                              className="adm-btn ghost sm"
                              onClick={(e) => { e.stopPropagation(); setSelected(r); }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                              Review
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="adm-pagination">
              <span>Page {page} of {totalPages}</span>
              <div className="adm-pag-btns">
                <button className="adm-pag-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                <button className="adm-pag-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
              </div>
            </div>
          </>
        )}
      </div>

      {selected && (
        <ReviewPanel item={selected} onClose={() => setSelected(null)} onDone={handleDone} />
      )}
    </AdminLayout>
  );
}
